import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  MDBBadge,
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalFooter,
  MDBModalHeader,
} from "mdbreact";
import { Services, Templates } from "../../../../../../../services/fakeDb";
import { fullName } from "../../../../../../../services/utilities";

/**
 * Groups the services of the cart by department + section (template).
 * A service included in multiple menus is listed once, with all its menus.
 */
const groupBySection = (cart = [], department) => {
  const services = {};

  cart.forEach(({ abbreviation, description, packages = [] }) => {
    Services.whereIn(packages.map(Number)).forEach((service) => {
      if (!services[service.id]) services[service.id] = { ...service, menus: [] };
      services[service.id].menus.push(abbreviation || description);
    });
  });

  const sections = {};
  Object.values(services).forEach((service) => {
    const dept = service.department || "OTHERS";
    const name =
      (Templates.collections.some(({ department: d }) => d === dept)
        ? Templates.getComponentName(service.template, dept)
        : undefined) || "Others";
    const key = `${dept}-${name}`;

    if (!sections[key])
      sections[key] = {
        key,
        name,
        dept,
        isOther: dept !== department,
        services: [],
      };
    sections[key].services.push(service);
  });

  // own department first, then by section name
  return Object.values(sections)
    .map((section) => ({
      ...section,
      services: section.services.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort(
      (a, b) =>
        Number(a.isOther) - Number(b.isOther) || a.name.localeCompare(b.name)
    );
};

/**
 * Shows everything the patient availed in this deal, grouped by section.
 */
export default function Requests({
  show,
  toggle,
  deal = {},
  department,
  isRendered = false,
  onPrint,
}) {
  const { cart = [], customerId = {} } = deal,
    [active, setActive] = useState("all");

  useEffect(() => {
    if (show) setActive("all");
  }, [show]);

  const sections = useMemo(
    () => groupBySection(cart, department),
    [cart, department]
  );

  const totalServices = sections.reduce(
    (total, { services }) => total + services.length,
    0
  );

  const filtered =
    active === "all" ? sections : sections.filter(({ key }) => key === active);

  const chipStyle = (isActive) => ({
    cursor: "pointer",
    border: "1px solid #007bff",
    borderRadius: 999,
    padding: "2px 10px",
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    backgroundColor: isActive ? "#007bff" : "transparent",
    color: isActive ? "#fff" : "#007bff",
  });

  // rendered on body so the card styles do not affect the modal
  return createPortal(
    <MDBModal isOpen={show} toggle={toggle} backdrop size="md">
      <MDBModalHeader
        toggle={toggle}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon="list" className="mr-2" />
        Requested Services
      </MDBModalHeader>
      <MDBModalBody style={{ maxHeight: "65vh", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center">
          <span>
            Patient: <b>{fullName(customerId?.fullName)}</b>
          </span>
          <small className="text-muted">
            {cart.length} item(s), {totalServices} test(s)
          </small>
        </div>

        {cart.length > 0 && (
          <div className="d-flex flex-wrap mt-1 mb-2" style={{ gap: 4 }}>
            {cart.map(({ referenceId, abbreviation, description }) => (
              <MDBBadge
                key={referenceId}
                color="primary"
                title={description}
                className="m-0"
              >
                {abbreviation || description}
              </MDBBadge>
            ))}
          </div>
        )}

        {sections.length > 1 && (
          <div
            className="d-flex flex-wrap pb-2 mb-2 border-bottom"
            style={{ gap: 6 }}
          >
            <span
              style={chipStyle(active === "all")}
              onClick={() => setActive("all")}
            >
              All ({totalServices})
            </span>
            {sections.map(({ key, name, dept, isOther, services }) => (
              <span
                key={key}
                style={chipStyle(active === key)}
                onClick={() => setActive(key)}
              >
                {isOther ? `${dept} · ` : ""}
                {name} ({services.length})
              </span>
            ))}
          </div>
        )}

        {!sections.length && (
          <p className="text-center text-muted my-3">No requested services.</p>
        )}

        {filtered.map(({ key, name, dept, isOther, services }) => (
          <div key={key} className="border rounded mb-2">
            <div
              className={`d-flex justify-content-between align-items-center px-2 py-1 ${
                isOther ? "grey lighten-3" : "blue lighten-5"
              }`}
            >
              <b className={isOther ? "text-muted" : "text-primary"}>
                <MDBIcon
                  icon={isOther ? "share" : "vials"}
                  className="mr-2"
                />
                {name}
              </b>
              <span>
                <small className="text-muted mr-2">
                  {services.length} test(s)
                </small>
                <MDBBadge
                  color={isOther ? "light" : "info"}
                  className={isOther ? "text-dark" : ""}
                >
                  {dept}
                </MDBBadge>
              </span>
            </div>
            <ul className="list-unstyled mb-0 px-2">
              {services.map(({ id, name, abbreviation, specimen, menus }) => (
                <li
                  key={id}
                  className={`d-flex justify-content-between small py-1 border-top ${
                    isOther ? "text-muted" : ""
                  }`}
                >
                  <span className="text-truncate mr-2">
                    {name}
                    {abbreviation && abbreviation !== name && (
                      <span className="text-muted"> ({abbreviation})</span>
                    )}
                    <br />
                    <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                      from: {menus.join(", ")}
                    </span>
                  </span>
                  {specimen && (
                    <span className="text-muted text-right">{specimen}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </MDBModalBody>
      <MDBModalFooter className="py-1">
        {isRendered && (
          <>
            <MDBBtn size="sm" color="primary" onClick={() => onPrint(false)}>
              <MDBIcon icon="barcode" className="mr-1" /> Barcode
            </MDBBtn>
            <MDBBtn size="sm" color="primary" onClick={() => onPrint(true)}>
              <MDBIcon icon="print" className="mr-1" /> Result Notes
            </MDBBtn>
          </>
        )}
        <MDBBtn size="sm" color="danger" onClick={toggle}>
          Close
        </MDBBtn>
      </MDBModalFooter>
    </MDBModal>,
    document.body
  );
}
